<template>
  <section class="development-details">
    <ErrorBucketViewer
      v-if="selectedBucket"
      :bucket="selectedBucket"
      :admin-id="adminId"
      :admin-name="adminName"
      @back="closeBucket"
      @resolved="handleBucketResolved"
    />

    <template v-else>
    <div class="details-toolbar">
      <div class="toolbar-controls">
        <label class="sort-control">
          <select
            v-model="sortType"
            aria-label="Sort error buckets"
          >
            <option value="most-recent">Most recent recorded</option>
            <option value="oldest-recorded">Oldest recorded</option>
            <option value="severity">Severity type</option>
            <option value="most-recorded">Most recorded</option>
            <option value="least-recorded">Least recorded</option>
          </select>
        </label>

        <details class="color-menu">
          <summary>
            Hide colors
            <span v-if="hiddenColors.length">
              ({{ hiddenColors.length }})
            </span>
          </summary>

          <div class="color-menu-options">
            <label
              v-for="color in colorOptions"
              :key="color.value"
              class="color-option"
            >
              <input
                v-model="hiddenColors"
                type="checkbox"
                :value="color.value"
              />

              <span
                class="color-swatch"
                :class="`swatch-${color.value}`"
                aria-hidden="true"
              ></span>

              <span>{{ color.label }}</span>
            </label>
          </div>
        </details>

        <button
          type="button"
          class="refresh-button"
          :disabled="loading"
          @click="loadErrors"
        >
          {{ loading ? "Loading..." : "Refresh" }}
        </button>
      </div>
    </div>

    <div
      v-if="loading"
      class="state-card"
    >
      Loading development errors...
    </div>

    <div
      v-else-if="errorMessage"
      class="state-card error"
    >
      {{ errorMessage }}
    </div>

    <div
      v-else-if="errorBuckets.length === 0"
      class="state-card empty"
    >
      No development errors were found.
    </div>

    <div
      v-else-if="displayedBuckets.length === 0"
      class="state-card empty"
    >
      No active error buckets match the current view.
    </div>

    <div
      v-else
      class="error-bucket-list"
    >
      <button
        v-for="bucket in displayedBuckets"
        :key="bucket.id"
        type="button"
        class="error-bucket"
        :class="bucketColorClass(bucket.count)"
        @click="openBucket(bucket)"
      >
        <span class="bucket-count">
          {{ bucket.count }}
        </span>

        <span class="bucket-copy">
          <strong class="bucket-error">
            {{ bucket.error }}
          </strong>

          <span class="bucket-location">
            {{ bucket.whErr }}
          </span>

          <span
            v-if="bucket.latestRecordedAt"
            class="bucket-recorded"
          >
            Last recorded {{ formatRecordedDate(bucket.latestRecordedAt) }}
          </span>
        </span>

        <span
          class="severity-pill"
          :class="bucket.severity"
        >
          {{ bucket.severity }}
        </span>
      </button>
    </div>
    </template>
  </section>
</template>

<script>
import {
  computed,
  onMounted,
  ref
} from "vue";

import {
  fetchDevelopmentErrorBucketStates,
  fetchDevelopmentErrors
} from "@/firebase/firestore";

import ErrorBucketViewer from "@/components/development/ErrorBucketViewer.vue";

export default {
  name: "DevelopmentDetailWindow",

  components: {
    ErrorBucketViewer
  },

  props: {
    adminId: {
      type: String,
      required: true
    },
    adminName: {
      type: String,
      required: true
    }
  },

  setup() {
    const errors =
      ref([]);

    const bucketStates =
      ref([]);

    const loading =
      ref(false);

    const errorMessage =
      ref("");

    const selectedBucket =
      ref(null);

    const sortType =
      ref("most-recorded");

    const hiddenColors =
      ref([]);

    const colorOptions = [
      { value: "red", label: "Red" },
      { value: "orange", label: "Orange" },
      { value: "yellow", label: "Yellow" },
      { value: "green", label: "Green" }
    ];

    const errorBuckets =
      computed(() => {
        const buckets = new Map();
        const states = new Map(
          bucketStates.value.map(bucketState => [
            JSON.stringify([
              bucketState.errorObject.error,
              bucketState.errorObject.whErr,
              bucketState.errorObject.severity
            ]),
            bucketState
          ])
        );

        errors.value.forEach(errorRecord => {
          const bucketId = JSON.stringify([
            errorRecord.error,
            errorRecord.whErr,
            errorRecord.severity
          ]);

          const existingBucket = buckets.get(bucketId);

          if (existingBucket) {
            existingBucket.count += 1;
            existingBucket.records.push(errorRecord);

            if (
              errorRecord.errRecordedAt &&
              (
                !existingBucket.latestRecordedAt ||
                errorRecord.errRecordedAt > existingBucket.latestRecordedAt
              )
            ) {
              existingBucket.latestRecordedAt = errorRecord.errRecordedAt;
            }

            return;
          }

          buckets.set(bucketId, {
            id: bucketId,
            error: errorRecord.error,
            whErr: errorRecord.whErr,
            severity: errorRecord.severity,
            count: 1,
            latestRecordedAt: errorRecord.errRecordedAt,
            records: [errorRecord]
          });
        });

        return Array.from(buckets.values()).map(bucket => ({
          ...bucket,
          state: states.get(bucket.id) ?? null
        }));
      });

    const displayedBuckets =
      computed(() => {
        const buckets = [...errorBuckets.value];
        const severityOrder = {
          critical: 0,
          error: 1,
          warning: 2,
          info: 3
        };

        const sortedBuckets = buckets.sort((first, second) => {
          let difference = 0;

          switch (sortType.value) {
          case "most-recent":
            difference = compareRecordedDates(first, second, "descending");
            break;

          case "oldest-recorded":
            difference = compareRecordedDates(first, second, "ascending");
            break;

          case "severity":
            difference =
              (severityOrder[first.severity] ?? 4) -
              (severityOrder[second.severity] ?? 4);
            break;

          case "least-recorded":
            difference = first.count - second.count;
            break;

          case "most-recorded":
          default:
            difference = second.count - first.count;
            break;
          }

          if (difference !== 0) {
            return difference;
          }

          return first.error.localeCompare(second.error);
        });

        return sortedBuckets.filter(bucket => {
          const isResolved =
            bucket.state?.status === "resolved" &&
            bucket.count <= bucket.state.resolvedOccurrenceCount;

          return (
            !isResolved &&
            !hiddenColors.value.includes(bucketColorType(bucket.count))
          );
        });
      });

    const compareRecordedDates =
      (first, second, direction) => {
        const firstTime = first.latestRecordedAt?.getTime?.() ?? null;
        const secondTime = second.latestRecordedAt?.getTime?.() ?? null;

        if (firstTime === null && secondTime === null) { return 0; }
        if (firstTime === null) { return 1; }
        if (secondTime === null) { return -1; }

        return direction === "ascending"
          ? firstTime - secondTime
          : secondTime - firstTime;
      };

    const bucketColorType =
      count => {
        if (count >= 11) { return "red"; }
        if (count >= 6) { return "orange"; }
        if (count >= 3) { return "yellow"; }

        return "green";
      };

    const bucketColorClass =
      count => `heat-${bucketColorType(count)}`;

    const formatRecordedDate =
      date => date.toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short"
      });

    const openBucket =
      bucket => {
        selectedBucket.value = bucket;
      };

    const closeBucket =
      () => {
        selectedBucket.value = null;
      };

    const handleBucketResolved =
      async () => {
        selectedBucket.value = null;
        await loadErrors();
      };

    const loadErrors =
      async () => {
        if (loading.value) { return; }

        loading.value = true;
        errorMessage.value = "";

        try {
          const [errorRecords, states] = await Promise.all([
            fetchDevelopmentErrors(),
            fetchDevelopmentErrorBucketStates()
          ]);

          errors.value = errorRecords;
          bucketStates.value = states;
        } catch (error) {
          console.error("❌ Unable to load development errors:", error);
          errorMessage.value = "Unable to load development errors.";
        } finally {
          loading.value = false;
        }
      };

    onMounted(loadErrors);

    return {
      errorBuckets,
      displayedBuckets,
      loading,
      errorMessage,
      selectedBucket,
      sortType,
      hiddenColors,
      colorOptions,
      bucketColorClass,
      formatRecordedDate,
      openBucket,
      closeBucket,
      handleBucketResolved,
      loadErrors
    };
  }
};
</script>

<style scoped>
.development-details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.details-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 20px;
}

.toolbar-controls {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
}

.sort-control {
  display: block;
}

.sort-control select {
  height: 40px;
  min-width: 190px;
  padding: 0 30px 0 10px;
  border: 1px solid #d8dedb;
  border-radius: 9px;
  background: #ffffff;
  color: #29342f;
  box-sizing: border-box;
  font: inherit;
  font-size: 0.9rem;
}

.color-menu {
  position: relative;
}

.color-menu summary {
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d8dedb;
  border-radius: 9px;
  background: #ffffff;
  color: #4f5b55;
  box-sizing: border-box;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  list-style: none;
}

.color-menu summary::-webkit-details-marker {
  display: none;
}

.color-menu summary::after {
  margin-left: 9px;
  border-top: 5px solid #68736d;
  border-right: 4px solid transparent;
  border-left: 4px solid transparent;
  content: "";
}

.color-menu[open] summary::after {
  transform: rotate(180deg);
}

.color-menu-options {
  position: absolute;
  z-index: 10;
  top: calc(100% + 7px);
  right: 0;
  display: flex;
  min-width: 170px;
  padding: 8px;
  border: 1px solid #d8dedb;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 8px 24px rgba(20, 40, 30, 0.13);
  flex-direction: column;
}

.color-option {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 9px;
  padding: 8px;
  border-radius: 7px;
  color: #3e4a44;
  font-size: 0.88rem;
  cursor: pointer;
}

.color-option:hover {
  background: #f4f7f5;
}

.color-option input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #159c62;
}

.color-swatch {
  width: 12px;
  height: 12px;
  border: 1px solid transparent;
  border-radius: 50%;
}

.swatch-red {
  border-color: rgba(195, 45, 58, 0.45);
  background: rgba(220, 53, 69, 0.45);
}

.swatch-orange {
  border-color: rgba(224, 112, 20, 0.45);
  background: rgba(243, 126, 32, 0.45);
}

.swatch-yellow {
  border-color: rgba(215, 165, 0, 0.45);
  background: rgba(245, 194, 24, 0.55);
}

.swatch-green {
  border-color: rgba(38, 160, 93, 0.45);
  background: rgba(38, 160, 93, 0.45);
}

.refresh-button {
  flex-shrink: 0;
  height: 40px;
  padding: 0 16px;
  border: 1px solid #d8dedb;
  border-radius: 9px;
  background: #ffffff;
  color: #29342f;
  box-sizing: border-box;
  font: inherit;
  font-weight: 600;
  cursor: pointer;
}

.refresh-button:disabled {
  cursor: default;
  opacity: 0.65;
}

.state-card {
  padding: 22px;
  border: 1px solid #e2e7e4;
  border-radius: 12px;
  background: #ffffff;
  color: #626d67;
}

.state-card.error {
  border-color: #efc4c4;
  background: #fff7f7;
  color: #a33636;
}

.error-bucket-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.error-bucket {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 18px;
  border: 1px solid #dde2df;
  border-radius: 12px;
  background: #ffffff;
  color: #26332d;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.error-bucket:hover {
  border-color: #aebbb4;
  box-shadow: 0 5px 16px rgba(20, 40, 30, 0.07);
}

.error-bucket.heat-green {
  border-color: rgba(38, 160, 93, 0.3);
  background: rgba(38, 160, 93, 0.08);
}

.error-bucket.heat-yellow {
  border-color: rgba(215, 165, 0, 0.35);
  background: rgba(245, 194, 24, 0.13);
}

.error-bucket.heat-orange {
  border-color: rgba(224, 112, 20, 0.35);
  background: rgba(243, 126, 32, 0.12);
}

.error-bucket.heat-red {
  border-color: rgba(195, 45, 58, 0.35);
  background: rgba(220, 53, 69, 0.11);
}

.bucket-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 42px;
  height: 42px;
  padding: 0 10px;
  border-radius: 10px;
  background: #eef4f0;
  color: #26332d;
  font-weight: 700;
}

.bucket-copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 6px;
}

.bucket-error,
.bucket-location {
  overflow-wrap: anywhere;
}

.bucket-error {
  line-height: 1.4;
}

.bucket-location {
  color: #737d78;
  font-size: 0.9rem;
}

.bucket-recorded {
  color: #89928e;
  font-size: 0.78rem;
}

.severity-pill {
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef1ef;
  color: #59645f;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.severity-pill.warning {
  background: #fff4d6;
  color: #916800;
}

.severity-pill.error,
.severity-pill.critical {
  background: #ffe4e4;
  color: #a52d2d;
}

.severity-pill.info {
  background: #e8f1ff;
  color: #315f9b;
}

@media (max-width: 700px) {
  .details-toolbar,
  .toolbar-controls {
    align-items: stretch;
    flex-direction: column;
  }

  .sort-control select {
    width: 100%;
  }

  .error-bucket {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .severity-pill {
    grid-column: 2;
    justify-self: start;
  }
}
</style>
